import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';

/**
 * DropdownBox Component - A Material-UI based dropdown alternative to HTML select
 * 
 * @param {string} label - Label displayed above the dropdown
 * @param {Array} options - Array of option objects with { id, name, description }
 * @param {Object} selectedOption - Currently selected option object
 * @param {Function} onSelect - Callback function when option is selected
 * @param {string} title - Dialog title
 * @param {boolean} required - Whether field is required
 */
const DropdownBox = ({
  label,
  options = [],
  selectedOption,
  onSelect,
  title = 'Select Option',
  required = false,
  fullWidth = false,
}) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (option) => {
    onSelect(option);
    setOpen(false);
  };

  const fontFamily = 'Zain, sans-serif';
  const fontWeight = 600;

  return (
    <>
      {label && (
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: 1, fontFamily, fontWeight: 700 }}>
          {label}
          {required && <span style={{ color: '#d32f2f', fontFamily, fontWeight }}> *</span>}
        </Typography>
      )}

      <Button
        fullWidth={fullWidth}
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{ justifyContent: 'flex-start', textTransform: 'none', padding: 2, fontFamily, fontWeight }}
      >
        <Box sx={{ textAlign: 'left', width: '100%', fontFamily, fontWeight }}>
          <Typography variant="body1" sx={{ fontFamily, fontWeight }}>
            {selectedOption?.name || 'Select...'}
          </Typography>
          {selectedOption?.description && (
            <Typography variant="caption" color="textSecondary" sx={{ fontFamily, fontWeight: 500 }}>
              {selectedOption.description}
            </Typography>
          )}
        </Box>
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { fontFamily, fontWeight }
        }}
      >
        <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>{title}</DialogTitle>
        <DialogContent sx={{ fontFamily, fontWeight }}>
          <List sx={{ fontFamily, fontWeight }}>
            {options.map((option) => (
              <ListItem key={option.id} disablePadding sx={{ fontFamily, fontWeight }}>
                <ListItemButton
                  selected={selectedOption?.id === option.id}
                  onClick={() => handleSelect(option)}
                  sx={{ fontFamily, fontWeight }}
                >
                  <ListItemText
                    primary={option.name}
                    secondary={option.description}
                    primaryTypographyProps={{ sx: { fontFamily, fontWeight: 600 } }}
                    secondaryTypographyProps={{ sx: { fontFamily, fontWeight: 500 } }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ fontFamily, fontWeight }}>
          <Button onClick={() => setOpen(false)} sx={{ fontFamily, fontWeight }}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DropdownBox;
